import { AlertTriangle, Check, Loader2, Send } from 'lucide-react';

import type { ProjectFormValues } from '../../schemas/project.schema';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { Sheet } from '@/shared/components/ui/sheet';
import { Textarea } from '@/shared/components/ui/textarea';
import { messages } from '@/shared/constants/messages';
import { leadTimeWarning } from '@/shared/constants/projectSizes';
import { formatDateTimeRange } from '@/shared/lib/dates';
import { toUserMessage } from '@/shared/lib/errors';
import { formatCurrency, formatList, pluralize } from '@/shared/lib/format';
import type {
  CatalogOption,
  MinistryMember,
  SpaceOption,
  SupportTypeOption,
  WritableMinistry,
} from '@/shared/types/domain';

/** Onde o envio está. `sent` é um estado da FOLHA, não da navegação. */
export type SubmitState = 'idle' | 'submitting' | 'sent';

interface ReviewSheetProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  state: SubmitState;
  error: unknown;
  values: ProjectFormValues;
  ministries: WritableMinistry[];
  members: MinistryMember[];
  spaces: SpaceOption[];
  categories: CatalogOption[];
  supportTypes: SupportTypeOption[];
  fundingSources: CatalogOption[];
  /** Modo ajuste (decisão 48): reenvio, não primeiro envio. */
  isAdjusting?: boolean;
  /**
   * A observação de reenvio (decisão 47). **O estado mora no documento**, e não
   * aqui: a folha desmonta ao fechar, e o texto tem de sobreviver a um "Voltar
   * e ajustar" — só sair da tela o descarta. Mesma promessa dos itens de apoio.
   */
  resubmitComment?: string;
  onResubmitCommentChange?: (value: string) => void;
}

/**
 * A revisão, e o envio de verdade.
 *
 * O resumo era um cartão no fim da seção 5, logo acima do botão — e ninguém
 * lia. Num documento de cinco seções, o que está acima do botão é o que a
 * pessoa rola para passar. Como folha, ele deixa de ser rodapé e vira PORTA:
 * é obrigatório atravessá-lo, ele chega por cima do que se está conferindo, e
 * o botão que envia mora só aqui dentro.
 *
 * A confirmação de envio também acontece aqui, e não na tela seguinte. Quem
 * aperta "Enviar" está entregando o projeto para outra pessoa decidir; ver a
 * tela trocar não é a mesma coisa que ver "enviado". Trocar primeiro e
 * confirmar depois é o que faz alguém enviar duas vezes.
 */
export function ReviewSheet({
  open,
  onClose,
  onConfirm,
  state,
  error,
  values,
  ministries,
  members,
  spaces,
  categories,
  supportTypes,
  fundingSources,
  isAdjusting = false,
  resubmitComment = '',
  onResubmitCommentChange,
}: ReviewSheetProps) {
  const warning = leadTimeWarning(values.expected_audience, values.starts_at);
  const isBusy = state !== 'idle';

  const ministry = ministries.find((entry) => entry.ministry_id === values.ministry_id);
  const category = categories.find((entry) => entry.id === values.category_id);
  const responsible = members.find(
    (member) => member.profile_id === values.responsible_profile_id,
  );
  const fundingSource = fundingSources.find((entry) => entry.id === values.funding_source_id);
  const selectedSpaces = spaces
    .filter((space) => values.space_ids.includes(space.id))
    .map((space) => space.name);
  const selectedSupport = values.support_items.flatMap((item) => {
    const type = supportTypes.find((entry) => entry.id === item.support_type_id);
    return type ? [type.name] : [];
  });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      // Enquanto a RPC corre e depois que ela volta, a folha não fecha por
      // engano: `Esc`, clique no fundo e arrasto ficam mudos. Fechar aqui
      // deixaria a pessoa sem saber se o projeto foi.
      dismissible={state === 'idle'}
      title={
        state === 'sent'
          ? isAdjusting
            ? messages.form.resentTitle
            : messages.form.sentTitle
          : isAdjusting
            ? messages.form.resubmitTitle
            : messages.form.reviewTitle
      }
      description={
        state === 'sent'
          ? undefined
          : isAdjusting
            ? messages.form.resubmitDescription
            : messages.form.reviewDescription
      }
      titleHidden={state === 'sent'}
      footer={
        state === 'sent' ? null : (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={isBusy}>
              {messages.form.reviewBack}
            </Button>
            <Button type="button" onClick={onConfirm} disabled={isBusy}>
              {state === 'submitting' ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : (
                <Send aria-hidden="true" className="size-4" />
              )}
              {state === 'submitting'
                ? isAdjusting
                  ? messages.form.resubmitting
                  : messages.form.submitting
                : isAdjusting
                  ? messages.form.resubmit
                  : messages.actions.submit}
            </Button>
          </div>
        )
      }
    >
      {state === 'sent' ? (
        <SentConfirmation isAdjusting={isAdjusting} />
      ) : (
        <div className="space-y-5 pb-1">
          {/* Duas colunas JÁ em 375px, e não só a partir de `sm`. Empilhado, o
              resumo virava uma coluna de dezoito linhas em que rótulo e valor
              têm o mesmo peso vertical — e a folha, que existe para ser lida de
              uma vez, passava a exigir três rolagens. */}
          <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-x-5">
            <SummaryRow label="Título" value={values.title} />
            <SummaryRow label="Ministério" value={ministry?.ministry_name ?? null} />
            <SummaryRow label="Categoria" value={category?.name ?? null} />
            <SummaryRow
              label="Quando"
              value={
                values.starts_at && values.ends_at
                  ? formatDateTimeRange(values.starts_at, values.ends_at)
                  : null
              }
            />
            <SummaryRow
              label="Onde"
              value={selectedSpaces.length > 0 ? formatList(selectedSpaces) : null}
            />
            <SummaryRow
              label="Público esperado"
              value={
                values.expected_audience
                  ? pluralize(values.expected_audience, 'pessoa', 'pessoas')
                  : null
              }
            />
            <SummaryRow label="Responsável" value={responsible?.full_name ?? null} />
            <SummaryRow
              label="Apoio"
              value={selectedSupport.length > 0 ? formatList(selectedSupport) : null}
            />
            <SummaryRow
              label="Orçamento"
              value={
                values.estimated_budget_cents !== null
                  ? [
                      formatCurrency(values.estimated_budget_cents),
                      fundingSource ? `via ${fundingSource.name}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : null
              }
            />
          </dl>

          {/* O objetivo em bloco, e não numa linha de `<dl>`: ele é o único
              campo longo do formulário e é o que a análise realmente lê. Com
              `line-clamp` a pessoa reveria um resumo do resumo. */}
          {values.description ? (
            <div className="space-y-1 border-t border-border pt-4">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Objetivo
              </h3>
              <p className="whitespace-pre-line text-sm leading-relaxed">{values.description}</p>
            </div>
          ) : null}

          {values.extra_info ? (
            <div className="space-y-1 border-t border-border pt-4">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Informações complementares
              </h3>
              <p className="whitespace-pre-line text-sm leading-relaxed">{values.extra_info}</p>
            </div>
          ) : null}

          {/* A observação de reenvio (decisão 47) — **abaixo do resumo e acima
              dos botões**, e só no modo ajuste: no envio inicial não existe "o
              que mudou", porque não havia nada antes.

              Sem contador e sem obrigatoriedade. Vazio manda `undefined` para
              a RPC, que é o que a aresta já aceita (`comment_required = false`
              desde agosto). Isto NÃO é comentário livre (PRD §5.2): o texto
              está preso a uma transição. */}
          {isAdjusting ? (
            <div className="space-y-1.5 border-t border-border pt-4">
              <Label htmlFor="observacao-de-reenvio">
                {messages.form.resubmitCommentLabel}
              </Label>
              <Textarea
                id="observacao-de-reenvio"
                rows={3}
                value={resubmitComment}
                disabled={isBusy}
                aria-describedby="observacao-de-reenvio-ajuda"
                onChange={(event) => onResubmitCommentChange?.(event.target.value)}
              />
              <p id="observacao-de-reenvio-ajuda" className="text-xs text-muted-foreground">
                {messages.form.resubmitCommentHint}
              </p>
            </div>
          ) : null}

          {/* Decisões 12 e 13: o alerta de porte × antecedência acompanhou o
              botão para cá e continua NÃO bloqueando. Ele fica encostado no
              rodapé fixo justamente para ser a última coisa lida antes do
              clique — a política se comunica, a decisão de enviar assim mesmo
              continua sendo humana. */}
          {warning ? (
            <div
              role="status"
              className="animate-reveal rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
            >
              <p className="flex items-center gap-2 font-medium text-amber-900">
                <AlertTriangle aria-hidden="true" className="size-4 shrink-0" />
                {messages.form.leadTimeTitle}
              </p>
              <p className="mt-1 text-amber-900/90">
                Este é um projeto de {warning.size.label} e faltam{' '}
                {warning.leadTimeDays >= 0
                  ? pluralize(warning.leadTimeDays, 'dia', 'dias')
                  : 'zero dias (a data já passou)'}
                . A política da igreja pede {warning.size.expectedLeadTimeDays} dias de
                antecedência. Você pode enviar assim mesmo.
              </p>
            </div>
          ) : null}

          {error ? (
            <p
              role="alert"
              className="animate-reveal rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {toUserMessage(error)}
            </p>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}

/**
 * "Foi." O visto cresce uma vez (`animate-pop`, a curva reservada a
 * confirmação) e a folha para. Sem confete e sem segundo movimento: o que a
 * pessoa precisa agora é da certeza, e a tela do projeto entra logo em
 * seguida — animação que continua depois disso vira concorrência com a
 * navegação.
 */
function SentConfirmation({ isAdjusting }: { isAdjusting: boolean }) {
  return (
    <div role="status" className="flex flex-col items-center gap-3 px-2 pb-2 pt-6 text-center">
      <span className="flex size-14 animate-pop items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200">
        <Check aria-hidden="true" className="size-7" />
      </span>
      <div className="animate-fade-in space-y-1">
        <p className="text-lg font-semibold tracking-tight">
          {isAdjusting ? messages.form.resentTitle : messages.form.sentTitle}
        </p>
        <p className="mx-auto max-w-xs text-sm text-muted-foreground">
          {isAdjusting ? messages.form.resentHint : messages.form.sentHint}
        </p>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <dt className="text-muted-foreground sm:text-right">{label}</dt>
      <dd className={value ? 'font-medium' : 'text-muted-foreground/60'}>
        {value ?? 'não preenchido'}
      </dd>
    </>
  );
}
